import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"
import type { AiConversation, AiMessage } from "@/lib/ai-types"

export function useAiConversations() {
  return useQuery({
    queryKey: ["ai_conversations"],
    queryFn: () => api.get<{ data: AiConversation[] }>("/api/v1/ai_conversations"),
  })
}

export function useAiConversation(id: string | null) {
  return useQuery({
    queryKey: ["ai_conversations", id],
    queryFn: () =>
      api.get<{ data: AiConversation; messages: AiMessage[] }>(
        `/api/v1/ai_conversations/${id}`
      ),
    enabled: !!id,
  })
}

export function useCreateAiConversation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      context_type?: string
      context_id?: string
      metadata?: Record<string, unknown>
    }) => api.post<{ data: AiConversation }>("/api/v1/ai_conversations", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai_conversations"] })
    },
  })
}

export function useSendAiMessage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      conversationId,
      content,
      currentPage,
      contextLabel,
    }: {
      conversationId: string
      content: string
      currentPage?: string
      contextLabel?: string
    }) =>
      api.post<{ data: AiMessage; conversation: AiConversation }>(
        `/api/v1/ai_conversations/${conversationId}/message`,
        { content, current_page: currentPage, context_label: contextLabel }
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["ai_conversations", variables.conversationId],
      })
    },
  })
}

export function useDeleteAiConversation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/ai_conversations/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai_conversations"] })
    },
  })
}
